import { useMemo } from 'react';
import { areSearchResultsTheSameMeal } from '../../services/meals';
import {
  useGetDistilledMealsByParentsQuery,
  useGetSharedBoardsByIDsQuery,
  useGetDistilledSharedMealsByParentsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetHealthProBoardsQuery,
  useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

export const useMultiMenuMeals = (menusWithParents) => {
  const {
    data: spaceAssignedSharedRecipesBoardsIDs,
    isLoading: isSaceAssignedSharedRecipesBoardsIDsLoading,
  } = useGetSpaceAssignedSharedRecipesBoardIDsForEndUserSearchQuery();

  const { data: healthProBoards } = useGetHealthProBoardsQuery();

  const { data: sharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedRecipesBoardsIDs,
    {
      skip:
        isSaceAssignedSharedRecipesBoardsIDsLoading ||
        !spaceAssignedSharedRecipesBoardsIDs ||
        spaceAssignedSharedRecipesBoardsIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const healthProBoardIDs = useMemo(
    () => (healthProBoards || []).map((b) => b.id),
    [healthProBoards],
  );

  const sharedBoardIDs = useMemo(
    () => (sharedBoards || []).map((b) => b.id),
    [sharedBoards],
  );

  const {
    data: distilledHealthProMealsByParents,
    isLoading: isDistilledHealthProMealsByParentsLoading,
  } = useGetDistilledMealsByParentsQuery(healthProBoardIDs, {
    skip: !healthProBoardIDs || healthProBoardIDs.length === 0,
  });

  const {
    data: distilledSharedMealsByParents,
    isLoading: isDistilledSharedMealsByParentsLoading,
  } = useGetDistilledSharedMealsByParentsQuery(sharedBoardIDs, {
    skip: !sharedBoardIDs || sharedBoardIDs.length === 0,
  });

  const areDistilledMealsLoading =
    isDistilledHealthProMealsByParentsLoading ||
    isDistilledSharedMealsByParentsLoading;

  const dedupedMergedEntriesFromMultipleMenus = useMemo(() => {
    const entryDistinctMeals = [];
    const dedupedMergedEntries = [];
    const mergedEntriesFromMultipleMenus =
      menusWithParents &&
      menusWithParents.length > 0 &&
      menusWithParents.flatMap((menuWithParent) => {
        let board = (healthProBoards || []).find(
          (b) => b.id === menuWithParent.parentID,
        );
        if (!board) {
          board = (sharedBoards || []).find(
            (b) => b.id === menuWithParent.parentID,
          );
        }
        if (!board) {
          return [];
        }
        const menu = board.menus.find((m) => m.id === menuWithParent.menuID);
        return (menu.mealIDs || menu.sharedMealIDs).map((mealID) => ({
          mealID,
          menuID: menu.id,
          parentID: board.id,
          isDeleteEnabled: false,
        }));
      });

    if (areDistilledMealsLoading) {
      // Can't run deduplication until we have all the distilled meals
      return mergedEntriesFromMultipleMenus;
    }

    // Now make sure that, after flattening, the meals are "unique"
    (mergedEntriesFromMultipleMenus || []).forEach((entry) => {
      const distilledSharedMeals =
        (distilledHealthProMealsByParents || {})[entry.parentID] ||
        (distilledSharedMealsByParents || {})[entry.parentID] ||
        {};
      const distilledMeal = distilledSharedMeals[entry.mealID];
      if (!distilledMeal) {
        console.log(`Skipping meal ID ${entry.mealID}, null meal.`);
        return;
      }
      if (
        !entryDistinctMeals.some((existingMeal) =>
          areSearchResultsTheSameMeal(
            existingMeal.recipes[0],
            distilledMeal.recipes[0],
          ),
        )
      ) {
        entryDistinctMeals.push(distilledMeal);
        dedupedMergedEntries.push(entry);
      } else {
        console.log(
          `Skipping meal ID ${entry.mealID}, similar to an existing meal.`,
        );
      }
    });
    return dedupedMergedEntries;
  }, [
    areDistilledMealsLoading,
    distilledHealthProMealsByParents,
    distilledSharedMealsByParents,
    healthProBoards,
    menusWithParents,
    sharedBoards,
  ]);

  const allBoards = useMemo(
    () => [...(healthProBoards || []), ...(sharedBoards || [])],
    [healthProBoards, sharedBoards],
  );

  const multiMenusTitle =
    menusWithParents &&
    menusWithParents.length > 0 &&
    (
      allBoards.find((b) => b.id === menusWithParents[0].parentID)?.menus || []
    ).find((m) => m.id === menusWithParents[0].menuID).title;

  const memoizedReturnValue = useMemo(
    () => ({
      dedupedMergedEntriesFromMultipleMenus,
      multiMenusTitle,
    }),
    [dedupedMergedEntriesFromMultipleMenus, multiMenusTitle],
  );

  return memoizedReturnValue;
};
